import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import {
    startOfMonth,
    endOfMonth,
    addDays,
    format,
    isSameDay,
    addMonths,
    startOfWeek,
    endOfWeek,
    addWeeks,
    differenceInCalendarDays,
    sub,
} from "date-fns";
import { FaChevronLeft, FaChevronRight, FaHome, FaUsers } from "react-icons/fa";
import ManagerNavbar from "../Layout/ManagerNavbar";
import axios from "axios";
import API_BASE_URL from "../config";

const TeamTimesheetGrid = () => {
    // const { managerId } = useParams();
    const managerId = sessionStorage.getItem("employeeId");

    const navigate = useNavigate();

    const [employees, setEmployees] = useState([]);
    const [selectedEmployee, setSelectedEmployee] = useState(null);
    const [worklogs, setWorklogs] = useState([]);
    const [tasks, setTasks] = useState([]);
    const [viewMode, setViewMode] = useState("monthly");
    const [billableFilter, setBillableFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const [startDate, setStartDate] = useState(() => startOfMonth(new Date()));
    const [endDate, setEndDate] = useState(() => endOfMonth(new Date()));
    const [error, setError] = useState("");
    const [holidayDates, setHolidayDates] = useState([]);
    const [holidayMap, setHolidayMap] = useState({});
    const [weeklyHolidays, setWeeklyHolidays] = useState([]);
    const invalidDateToastShown = useRef(false);
    const [leaveDates, setLeaveDates] = useState([]);

    const token = sessionStorage.getItem("token");
    const employeeId = sessionStorage.getItem("employeeId"); // acts as reporter/manager id


   useEffect(() => {
    if (!token || !employeeId) {
        console.error("Missing token or employeeId");
        navigate("/");
        return;
    }

    const fetchEmployees = async () => {
        try {
            const res = await axios.get(
                `${API_BASE_URL}/api/manageteam/reporter/${employeeId}`,
                { headers: { Authorization: `Bearer ${token}` } }
            );

            let raw = res.data;
            const arr = Array.isArray(raw) ? raw : (raw ? [raw] : []);

            // Extract nested employee
            let employeesList = arr
                .map((item) => item?.employee || null)
                .filter(Boolean);

            // *** REMOVE DUPLICATES BY EMPLOYEE ID ***
            employeesList = Array.from(
                new Map(
                    employeesList.map(emp => [emp.id, emp])
                ).values()
            );

            console.debug("manageteam/reporter payload:", res.data);
            console.debug("unique employees:", employeesList);

            setEmployees(employeesList);
            setSelectedEmployee(null);
        } catch (err) {
            console.error("Failed to fetch employees (manageteam):", err);
            setError("Unable to load your team members.");
            setEmployees([]);
            setSelectedEmployee(null);
        }
    };

    fetchEmployees();
}, [token, employeeId, navigate]);



    useEffect(() => {
        const fetchLeaves = async () => {
            const token = sessionStorage.getItem("token");
            let leaveList = [];

            if (!selectedEmployee) {
                // ALL EMPLOYEES
                for (let emp of employees) {
                    const res = await axios.get(`${API_BASE_URL}/api/leaves/employee/${emp.id}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    res.data.forEach(lv => {
                        const start = new Date(lv.startDate);
                        const end = new Date(lv.endDate);
                        for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                            leaveList.push({ employeeId: emp.id, date: d.toISOString().split("T")[0] });
                        }
                    });
                }
            } else {
                // Single employee
                const res = await axios.get(`${API_BASE_URL}/api/leaves/employee/${selectedEmployee.id}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                res.data.forEach(lv => {
                    const start = new Date(lv.startDate);
                    const end = new Date(lv.endDate);
                    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
                        leaveList.push({ employeeId: selectedEmployee.id, date: d.toISOString().split("T")[0] });
                    }
                });
            }

            setLeaveDates(leaveList);
        };

        fetchLeaves();
    }, [selectedEmployee, employees]);


    const isLeaveForEmployee = (employeeId, day) => {
        const formatted = format(day, "yyyy-MM-dd");
        return leaveDates.some(ld => ld.employeeId === employeeId && ld.date === formatted);
    };


    useEffect(() => {
        if (!token || !employeeId) return;

        const headers = { Authorization: `Bearer ${token}` };

        const fetchWorklogsForAll = async (subs) => {
            if (!subs || subs.length === 0) return [];
            const promises = subs.map((emp) =>
                axios
                    .get(`${API_BASE_URL}/api/worklogs/employee/${emp.id}`, {
                        headers,
                    })
                    .then((r) => r.data || [])
                    .catch((err) => {
                        console.error("Error fetching logs for employee", emp.id, err);
                        return [];
                    })
            );

            const results = await Promise.all(promises);
            return results.flat(); // merge all worklogs
        };

        const fetchData = async () => {
            setLoading(true);
            setError("");

            try {
                // 👉 CASE 1: ALL EMPLOYEES (selectedEmployee === null)
                if (selectedEmployee === null) {
                    const combinedLogs = await fetchWorklogsForAll(employees);
                    setWorklogs(combinedLogs);
                    setTasks([]);
                    return;
                }

                // 👉 CASE 2: SPECIFIC EMPLOYEE
                if (selectedEmployee?.id) {
                    const [logRes, taskRes] = await Promise.all([
                        axios.get(
                            `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}`,
                            { headers }
                        ),
                        axios
                            .get(
                                `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}/tasks`,
                                { headers }
                            )
                            .catch(() => ({ data: [] })),
                    ]);

                    setWorklogs(logRes.data || []);
                    setTasks(taskRes.data || []);
                    return;
                }
            } catch (err) {
                console.error("Worklog fetch error:", err);
                setError("Unable to load timesheet data.");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [selectedEmployee, employees, token, employeeId]);




    // Load Bootstrap dynamically
    useEffect(() => {
        // Bootstrap CSS
        const bootstrapLink = document.createElement("link");
        bootstrapLink.rel = "stylesheet";
        bootstrapLink.href = "https://cdn.jsdelivr.net/npm/bootstrap@5.3.2/dist/css/bootstrap.min.css";
        bootstrapLink.id = "timesheetgrid-bootstrap";
        document.head.appendChild(bootstrapLink);

        // Bootstrap Icons CSS
        const iconsLink = document.createElement("link");
        iconsLink.rel = "stylesheet";
        iconsLink.href = "https://cdn.jsdelivr.net/npm/bootstrap-icons@1.11.1/font/bootstrap-icons.css";
        iconsLink.id = "timesheetgrid-icons";
        document.head.appendChild(iconsLink);

        return () => {
            const existingBootstrap = document.getElementById("timesheetgrid-bootstrap");
            if (existingBootstrap) existingBootstrap.remove();

            const existingIcons = document.getElementById("timesheetgrid-icons");
            if (existingIcons) existingIcons.remove();
        };
    }, []);


    useEffect(() => {
        const fetchWeeklyHolidays = async () => {
            try {
                // Example endpoint → adjust to your actual backend API
                const res = await axios.get(`${API_BASE_URL}/api/holidays/weekly`);
                const days = res.data.map(h => h.name); // e.g., ["Sunday", "Saturday"]
                setWeeklyHolidays(days);
                console.log("✅ Weekly holidays loaded:", days);
            } catch (err) {
                console.error("Failed to fetch weekly holidays:", err);
            }
        };

        fetchWeeklyHolidays();
    }, []);


    useEffect(() => {
        const fetchHolidays = async () => {
            try {
                const res = await axios.get(`${API_BASE_URL}/api/holidays`);
                const holidays = res.data || [];

                const dates = [];
                const map = {};

                holidays.forEach((h) => {
                    const start = new Date(h.startDate);
                    const end = new Date(h.endDate);
                    const daysBetween =
                        (end - start) / (1000 * 60 * 60 * 24) + 1;

                    for (let i = 0; i < daysBetween; i++) {
                        const d = new Date(start);
                        d.setDate(start.getDate() + i);
                        const formatted = format(d, "yyyy-MM-dd");
                        dates.push(formatted);
                        map[formatted] = h.name;
                    }
                });

                setHolidayDates(dates);
                setHolidayMap(map);
                console.log("🎉 Holidays loaded:", dates);
            } catch (err) {
                console.error("Failed to fetch holidays:", err);
            }
        };

        fetchHolidays();
    }, []);


    // Load worklogs and tasks when employee changes
    // useEffect(() => {
    //     const token = sessionStorage.getItem("token");
    //     const empId = sessionStorage.getItem("employeeId");
    //     if (!token || !empId) return;

    //     const fetchWorklogsAndTasks = async () => {
    //         setLoading(true);
    //         try {
    //             if (selectedEmployee && selectedEmployee.id) {
    //                 const logsRes = await axios.get(
    //                     `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}`,
    //                     { headers: { Authorization: `Bearer ${token}` } }
    //                 );
    //                 setWorklogs(Array.isArray(logsRes.data) ? logsRes.data : []);

    //                 const tasksRes = await axios.get(
    //                     `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}/tasks`,
    //                     { headers: { Authorization: `Bearer ${token}` } }
    //                 );
    //                 setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
    //             } else {
    //                 const logsRes = await axios.get(
    //                     `${API_BASE_URL}/api/worklogs/manager/${empId}`,
    //                     { headers: { Authorization: `Bearer ${token}` } }
    //                 );
    //                 setWorklogs(Array.isArray(logsRes.data) ? logsRes.data : []);
    //                 setTasks([]);
    //             }
    //         } catch (err) {
    //             console.error("Error fetching worklogs:", err);
    //             setError("Unable to fetch timesheet data.");
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     fetchWorklogsAndTasks();
    // }, [selectedEmployee, employees]);



    // --- Remove the other worklog useEffect blocks and replace with this single one ---
    useEffect(() => {
        const token = sessionStorage.getItem("token");
        const managerId = sessionStorage.getItem("employeeId");
        if (!token || !managerId) return;

        const headers = { Authorization: `Bearer ${token}` };

        const fetchAllSubordinatesWorklogsFallback = async (subs) => {
            // fallback: fetch each employee's worklogs in parallel and merge
            if (!Array.isArray(subs) || subs.length === 0) return [];
            const promises = subs.map((emp) =>
                axios
                    .get(`${API_BASE_URL}/api/worklogs/employee/${emp.id}`, { headers })
                    .then((r) => (Array.isArray(r.data) ? r.data : []))
                    .catch((err) => {
                        console.error(`Failed to fetch logs for employee ${emp.id}`, err);
                        return [];
                    })
            );
            const results = await Promise.all(promises);
            // flatten
            return results.flat();
        };

        const load = async () => {
            setLoading(true);
            setError("");
            try {
                // If a single employee is selected -> fetch that employee's logs & tasks
                if (selectedEmployee && selectedEmployee.id) {
                    const [logsRes, tasksRes] = await Promise.all([
                        axios.get(`${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}`, { headers }),
                        axios.get(`${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}/tasks`, { headers }).catch(() => ({ data: [] })),
                    ]);
                    setWorklogs(Array.isArray(logsRes.data) ? logsRes.data : []);
                    setTasks(Array.isArray(tasksRes.data) ? tasksRes.data : []);
                    return;
                }

                // selectedEmployee === null -> "All subordinates"
                // First try: a backend endpoint that returns ALL subordinates' worklogs in one response
                try {
                    const allLogsRes = await axios.get(
                        `${API_BASE_URL}/api/worklogs/employee-subordinates?reporterId=${managerId}`,
                        { headers }
                    );

                    // Heuristic: check what's returned
                    const payload = allLogsRes.data;
                    if (Array.isArray(payload)) {
                        // if array and looks like worklogs (has employeeId/date/hours), use it
                        const looksLikeWorklogs = payload.length > 0 && payload[0] && (payload[0].employeeId !== undefined || payload[0].date !== undefined);
                        if (looksLikeWorklogs) {
                            setWorklogs(payload);
                            setTasks([]); // no consolidated tasks endpoint used here
                            return;
                        }

                        // if payload is array of employees (has id, firstName etc) => fallback
                        const looksLikeEmployees = payload.length > 0 && payload[0] && payload[0].id && (payload[0].firstName || payload[0].lastName);
                        if (looksLikeEmployees) {
                            console.debug("employee-subordinates returned employee objects, falling back to per-employee fetch");
                            const combined = await fetchAllSubordinatesWorklogsFallback(payload);
                            setWorklogs(combined);
                            setTasks([]);
                            return;
                        }

                        // empty array => nothing to show
                        setWorklogs([]);
                        setTasks([]);
                        return;
                    } else {
                        // Not an array — unexpected shape; fallback to per-employee using local employees state
                        console.warn("employee-subordinates endpoint returned unexpected payload:", payload);
                    }
                } catch (err) {
                    // If endpoint doesn't exist or fails, fallback to per-employee using employees state
                    console.warn("Bulk subordinate worklogs endpoint failed or returned non-worklog data; falling back.", err);
                }

                // Final fallback: use the `employees` state (already fetched by earlier effect) to request each employee's worklogs
                if (Array.isArray(employees) && employees.length > 0) {
                    const combined = await fetchAllSubordinatesWorklogsFallback(employees);
                    setWorklogs(combined);
                    setTasks([]);
                } else {
                    // If employees not yet available, try to fetch subordinates list directly then fetch their logs
                    try {
                        const subsRes = await axios.get(`${API_BASE_URL}/api/tasks/employee-subordinates?reporterId=${managerId}`, { headers });
                        const subs = Array.isArray(subsRes.data) ? subsRes.data : [];
                        if (subs.length > 0) {
                            const combined = await fetchAllSubordinatesWorklogsFallback(subs);
                            setWorklogs(combined);
                        } else {
                            setWorklogs([]);
                        }
                    } catch (err) {
                        console.error("Failed to fetch subordinates for fallback:", err);
                        setWorklogs([]);
                    }
                    setTasks([]);
                }
            } catch (err) {
                console.error("Error loading worklogs:", err);
                setError("Unable to load timesheet data.");
            } finally {
                setLoading(false);
            }
        };

        load();
        // note: include employees because fallback uses it when available
    }, [selectedEmployee, employees, token, employeeId]);



    // useEffect(() => {
    //     const token = sessionStorage.getItem("token");
    //     const managerId = sessionStorage.getItem("employeeId"); // Manager’s own ID
    //     if (!token || !managerId) return;

    //     const fetchData = async () => {
    //         setLoading(true);
    //         try {
    //             let worklogsRes;

    //             if (selectedEmployee && selectedEmployee.id) {
    //                 // 🧍 Single employee
    //                 worklogsRes = await axios.get(
    //                     `${API_BASE_URL}/api/worklogs/employee/${selectedEmployee.id}`,
    //                     { headers: { Authorization: `Bearer ${token}` } }
    //                 );
    //             } else {
    //                 // 👥 All employees under the manager
    //                 worklogsRes = await axios.get(
    //                     `${API_BASE_URL}/api/worklogs/employee-subordinates?reporterId=${employeeId}`,
    //                     { headers: { Authorization: `Bearer ${token}` } }
    //                 );

    //             }

    //             setWorklogs(Array.isArray(worklogsRes.data) ? worklogsRes.data : []);
    //         } catch (error) {
    //             console.error("Error fetching worklogs:", error);
    //             setError("Unable to load timesheet data.");
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     fetchData();
    // }, [selectedEmployee]);


    const isWeeklyHoliday = (day) => {
        const dayName = format(day, "EEEE"); // e.g., "Monday"
        return weeklyHolidays.includes(dayName);
    };



    const handleExport = async (fileFormat) => {
        if (!selectedEmployee) return;
        const token = sessionStorage.getItem("token");
        if (!token) return;

        try {
            // Format dates as yyyy-MM-dd
            const fromDateStr = startDate ? startDate.toISOString().split("T")[0] : "";
            const toDateStr = endDate ? endDate.toISOString().split("T")[0] : "";

            // Build URL params
            const params = new URLSearchParams();
            params.append("type", "date"); // must be "date" for fromDate/toDate filter
            if (fromDateStr) params.append("fromDate", fromDateStr);
            if (toDateStr) params.append("toDate", toDateStr);

            // Add billing type separately as a query param if needed
            if (billableFilter && billableFilter !== "all") params.append("billingType", billableFilter);

            const response = await axios.get(
                `${API_BASE_URL}/api/reports/timesheet/${selectedEmployee.id}?${params.toString()}&format=${fileFormat}`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: "blob",
                }
            );

            const blob = new Blob([response.data], {
                type:
                    fileFormat === "pdf"
                        ? "application/pdf"
                        : "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            });

            const url = window.URL.createObjectURL(blob);
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute(
                "download",
                `Timesheet_${selectedEmployee.firstName}_${selectedEmployee.lastName}.${fileFormat === "pdf" ? "pdf" : "xlsx"
                }`
            );
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (err) {
            console.error("Export failed:", err);
            alert("Failed to export report. Please try again.");
        }
    };

    // Generate dynamic days
    const dayCount = differenceInCalendarDays(endDate, startDate) + 1;
    const days = Array.from({ length: dayCount }, (_, i) => addDays(startDate, i));

    const handlePrev = () => {
        if (viewMode === "monthly") {
            const newStart = sub(startDate, { months: 1 });
            setStartDate(newStart);
            setEndDate(endOfMonth(newStart));
        } else {
            const newStart = sub(startDate, { weeks: 1 });
            setStartDate(newStart);
            setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
        }
    };

    const handleNext = () => {
        if (viewMode === "monthly") {
            const newStart = addMonths(startDate, 1);
            setStartDate(newStart);
            setEndDate(endOfMonth(newStart));
        } else {
            const newStart = addWeeks(startDate, 1);
            setStartDate(newStart);
            setEndDate(endOfWeek(newStart, { weekStartsOn: 1 }));
        }
    };

    const filteredWorklogs = (() => {
        if (!startDate || !endDate) return [];

        if (endDate < startDate) {
            if (!invalidDateToastShown.current) {
                toast.error("End date must be greater than or equal to Start date.");
                invalidDateToastShown.current = true;
            }
            return [];
        } else {
            invalidDateToastShown.current = false; // reset when valid
        }

        return worklogs.filter((log) => {
            const logDate = new Date(log.date);
            const inDateRange = logDate >= startDate && logDate <= endDate;
            if (!inDateRange) return false;

            if (billableFilter === "all") return true;
            return log.billingType === billableFilter;
        });
    })();


    // Row total for each employee
    const getRowTotal = (employeeId) =>
        days.reduce((sum, day) => sum + getHours(employeeId, day), 0);

    // Column total for each day & type
    const getColumnTotal = (day, type) =>
        employees.reduce((sum, emp) => sum + getHours(emp.id, day, type), 0);


    const getHours = (employeeId, day, billableType) =>
        filteredWorklogs
            .filter(
                (log) =>
                    log.employeeId === employeeId &&
                    isSameDay(new Date(log.date), day) &&
                    (!billableType || log.billingType === billableType)
            )
            .reduce((sum, log) => sum + log.hours, 0);


    const totalHours = filteredWorklogs.reduce((sum, log) => sum + log.hours, 0);
    const totalBillable = filteredWorklogs
        .filter((log) => log.billingType === "billable")
        .reduce((sum, log) => sum + log.hours, 0);
    const totalNonBillable = totalHours - totalBillable;

    const totalFilteredHours = filteredWorklogs.reduce((sum, log) => sum + log.hours, 0);
    const totalFilteredBillable = filteredWorklogs
        .filter((log) => log.billingType === "billable")
        .reduce((sum, log) => sum + log.hours, 0);
    const totalFilteredNonBillable = filteredWorklogs
        .filter((log) => log.billingType === "non-billable")
        .reduce((sum, log) => sum + log.hours, 0);

    if (loading)
        return (
            <div className="d-flex justify-content-center align-items-center mt-5">
                <div className="spinner-border text-primary"></div>
                <span className="ms-2 fs-5">Loading timesheet...</span>
            </div>
        );

    return (
        <>
            <ToastContainer position="top-right" autoClose={4000} />
            <div className="fixed-top">
                <ManagerNavbar />
            </div>
            <div className="container-fluid mt-4" style={{ paddingTop: "110px" }}>
                {/* Header */}
                <div className="d-flex flex-wrap justify-content-between align-items-center p-3 bg-white rounded shadow-sm border gap-3">
                    {/* Employee Selector */}
                    {/* Employee Selector */}
                    <div className="d-flex align-items-center gap-2 mb-3">
                        <FaUsers className="text-primary fs-4" />
                        <select
                            className="form-select form-select-sm"
                            style={{ minWidth: "200px" }}
                            value={selectedEmployee?.id || ""}
                            onChange={(e) => {
                                const value = e.target.value;
                                if (value === "") {
                                    setSelectedEmployee(null); // all employees
                                } else {
                                    const emp = employees.find(emp => emp.id === Number(value));
                                    setSelectedEmployee(emp || null);
                                }
                            }}
                        >
                            <option value="">All Employees</option>
                            {employees.map((emp) => (
                                <option key={emp.id} value={emp.id}>
                                    {emp.firstName} {emp.lastName}
                                </option>
                            ))}
                        </select>
                    </div>



                    {/* Controls */}
                    <div className="d-flex align-items-center gap-2 flex-wrap">
                        <button
                            className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                            onClick={() => navigate(-1)}
                        >
                            <FaChevronLeft /> Back
                        </button>
                        {/* <button
                            className="btn btn-sm btn-outline-dark d-flex align-items-center gap-1"
                            onClick={() => navigate("/managerdashboard")}
                        >
                            <FaHome /> Home
                        </button> */}

                        <button
                            className={`btn btn-sm ${viewMode === "monthly" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => {
                                setViewMode("monthly");
                                setStartDate(startOfMonth(startDate));
                                setEndDate(endOfMonth(startDate));
                            }}
                        >
                            Monthly
                        </button>
                        <button
                            className={`btn btn-sm ${viewMode === "weekly" ? "btn-primary" : "btn-outline-primary"}`}
                            onClick={() => {
                                const today = new Date(); // Get today's date
                                setViewMode("weekly");
                                setStartDate(startOfWeek(today, { weekStartsOn: 1 })); // Monday as start of week
                                setEndDate(endOfWeek(today, { weekStartsOn: 1 }));     // Sunday as end of week
                            }}
                        >
                            Weekly
                        </button>

                        <div className="btn-group btn-group-sm">
                            <button
                                className={`btn btn-sm ${billableFilter === "all" ? "btn-success" : "btn-outline-success"}`}
                                onClick={() => setBillableFilter("all")}
                            >
                                All
                            </button>
                            <button
                                className={`btn btn-sm ${billableFilter === "billable" ? "btn-success" : "btn-outline-success"}`}
                                onClick={() => setBillableFilter("billable")}
                            >
                                Billable
                            </button>
                            <button
                                className={`btn btn-sm ${billableFilter === "non-billable" ? "btn-success" : "btn-outline-success"}`}
                                onClick={() => setBillableFilter("non-billable")}
                            >
                                Non-Billable
                            </button>
                        </div>

                        <div className="btn-group btn-group-sm">
                            <button className="btn btn-outline-primary" onClick={handlePrev}>
                                <FaChevronLeft />
                            </button>
                            <button className="btn btn-outline-primary" onClick={handleNext}>
                                <FaChevronRight />
                            </button>
                        </div>
                        {/* Date Range */}
                        <div className="d-flex gap-1 align-items-center">
                            <input
                                type="date"
                                value={startDate ? format(startDate, "yyyy-MM-dd") : ""}
                                onChange={(e) =>
                                    setStartDate(e.target.value ? new Date(e.target.value) : null)
                                }
                                className="form-control form-control-sm"
                                style={{ width: "110px" }}
                            />
                            <span className="text-muted">to</span>
                            <input
                                type="date"
                                value={endDate ? format(endDate, "yyyy-MM-dd") : ""}
                                onChange={(e) =>
                                    setEndDate(e.target.value ? new Date(e.target.value) : null)
                                }
                                className="form-control form-control-sm"
                                style={{ width: "110px" }}
                            />
                        </div>
                        {/* Export Buttons */}
                        {/* <div className="btn-group btn-group-sm ms-2">
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => handleExport("pdf")}
                        >
                            Export PDF
                        </button>
                        <button
                            className="btn btn-outline-secondary"
                            onClick={() => handleExport("excel")}
                        >
                            Export Excel
                        </button>
                    </div> */}

                    </div>
                </div>

                {/* Compact Summary Cards */}
                <div className="row g-2 mb-3 mt-2">
                    {[
                        { title: "Total Hours", value: totalFilteredHours, bg: "primary", icon: "bi-hourglass-split" },
                        { title: "Billable", value: totalFilteredBillable, bg: "success", icon: "bi-cash-coin" },
                        { title: "Non-Billable", value: totalFilteredNonBillable, bg: "danger", icon: "bi-slash-circle" },
                    ].map((card, idx) => (
                        <div key={idx} className="col-12 col-sm-4 col-md-2">
                            <div className="card shadow-sm border-0 h-100 p-2">
                                <div className="d-flex align-items-center gap-2">
                                    {/* Icon */}
                                    <div
                                        className={`rounded-circle bg-${card.bg} bg-opacity-10 text-${card.bg} d-flex align-items-center justify-content-center`}
                                        style={{ width: "35px", height: "35px" }}
                                    >
                                        <i className={`bi ${card.icon} fs-6`}></i>
                                    </div>

                                    {/* Text */}
                                    <div className="flex-grow-1">
                                        <h6 className="mb-0 fw-semibold" style={{ fontSize: "0.85rem" }}>
                                            {card.title}
                                        </h6>
                                        <small className="text-muted" style={{ fontSize: "0.75rem" }}>
                                            {card.value} hrs
                                        </small>
                                    </div>

                                    {/* Value */}
                                    <h6 className={`mb-0 text-${card.bg} fw-bold`} style={{ fontSize: "0.9rem" }}>
                                        {card.value}
                                    </h6>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Timesheet Grid */}
                <div className="shadow-none rounded border mt-3" style={{ display: "flex", width: "100%" }}>
                    {/* Left Sticky Column */}
                    <div style={{ position: "sticky", left: 0, zIndex: 10, background: "white" }}>
                        <table className="table table-bordered mb-0 text-center">
                            <thead className="table-primary sticky-top">
                                <tr>
                                    <th style={{ minWidth: "180px", height: "65px" }}>Employee</th>
                                </tr>
                            </thead>
                            <tbody>
                                {/* Top empty row for alignment */}
                                <tr><span style={{ visibility: "hidden" }}>0</span></tr>

                                {employees.map((emp) => (
                                    <tr key={emp.id}>
                                        <td className="text-start fw-normal">{emp.firstName} {emp.lastName}</td>
                                    </tr>
                                ))}

                                {/* Bottom empty row for alignment */}
                                {/* <tr><td>&nbsp;</td></tr> */}
                            </tbody>
                            <tfoot className="fw-semibold table-light">
                                <tr><td>Daily Total</td></tr>
                            </tfoot>
                        </table>
                    </div>


                    {/* Right Sticky Column (Billable / Non-Billable / Total) */}
                    <div
                        style={{
                            position: "sticky",
                            right: 0,
                            zIndex: 10,
                            background: "white",
                        }}
                    >
                        <table className="table table-bordered mb-0 text-center">
                            <thead className="table-primary sticky-top">
                                <tr>
                                    <th colSpan={3} style={{ minWidth: "210px", height: "65px" }}>
                                        Total
                                    </th>
                                </tr>
                                <tr>
                                    <th className=" text-dark p-0 fw-normal">BL</th>
                                    <th className=" text-dark p-0 fw-normal">NBL</th>
                                    <th className=" text-dark p-0 fw-normal">Total</th>
                                </tr>
                            </thead>

                            <tbody>


                                {employees.map((emp) => {
                                    const totalBillable = days.reduce(
                                        (sum, day) => sum + getHours(emp.id, day, "billable"),
                                        0
                                    );
                                    const totalNonBillable = days.reduce(
                                        (sum, day) => sum + getHours(emp.id, day, "non-billable"),
                                        0
                                    );
                                    const total = totalBillable + totalNonBillable;

                                    return (
                                        <tr key={emp.id}>
                                            <td className="fw-semibold text-success">{totalBillable}</td>
                                            <td className="fw-semibold text-danger">{totalNonBillable}</td>
                                            <td className="fw-semibold text-primary">{total}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>

                            <tfoot className="fw-bold table-light">
                                <tr>
                                    <td className="text-success">
                                        {employees.reduce(
                                            (sum, emp) =>
                                                sum +
                                                days.reduce(
                                                    (daySum, day) => daySum + getHours(emp.id, day, "billable"),
                                                    0
                                                ),
                                            0
                                        )}
                                    </td>
                                    <td className="text-danger">
                                        {employees.reduce(
                                            (sum, emp) =>
                                                sum +
                                                days.reduce(
                                                    (daySum, day) =>
                                                        daySum + getHours(emp.id, day, "non-billable"),
                                                    0
                                                ),
                                            0
                                        )}
                                    </td>
                                    <td className="text-primary">
                                        {employees.reduce(
                                            (sum, emp) =>
                                                sum +
                                                days.reduce((daySum, day) => daySum + getHours(emp.id, day), 0),
                                            0
                                        )}
                                    </td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>

                    {/* Middle Scrollable Section */}
                    <div style={{ overflow: "auto", flex: 1 }}>
                        <table className="table table-bordered mb-0 text-center" style={{ minWidth: days.length * 210 }}>
                            <thead className="table-primary sticky-top">
                                <tr>
                                    {days.map((day, i) => {
                                        const formatted = format(day, "yyyy-MM-dd");
                                        const isHoliday = holidayDates.includes(formatted);
                                        const isWeekly = isWeeklyHoliday(day);

                                        const bgColor = isHoliday ? "#d4f8d4" : isWeekly ? "#f2f2f2" : "";
                                        const borderColor = isHoliday ? "#e0e0e0" : isWeekly ? "#e0e0e0" : "";
                                        const tooltip = isHoliday
                                            ? holidayMap[formatted]
                                            : isWeekly
                                                ? "Weekly Holiday"
                                                : "";

                                        return (
                                            <th
                                                key={i}
                                                colSpan={2} // ✅ Two columns (Billable + Non-Billable)
                                                className="text-center fw-normal"
                                                style={{
                                                    minWidth: "70px",
                                                    backgroundColor: bgColor,
                                                    borderColor,

                                                }}
                                                title={tooltip}
                                            >
                                                {format(day, "d")}
                                                <br />
                                                <small
                                                    className={isHoliday ? "text-secondary fw-semibold" : "text-muted"}
                                                >
                                                    {format(day, "EEE")}
                                                </small>
                                            </th>
                                        );
                                    })}



                                </tr>
                                <tr>
                                    {days.map((day, i) => {
                                        const formatted = format(day, "yyyy-MM-dd");
                                        const isHoliday = holidayDates.includes(formatted);
                                        const isWeekly = isWeeklyHoliday(day);
                                        const bgColor = isHoliday ? "#d4f8d4" : isWeekly ? "#f2f2f2" : "";
                                        const borderColor = isHoliday ? "#e0e0e0" : isWeekly ? "#e0e0e0" : "";

                                        const tooltip = isHoliday
                                            ? holidayMap[formatted]
                                            : isWeekly
                                                ? "Weekly Holiday"
                                                : "";

                                        return (
                                            <React.Fragment key={i}>
                                                <th
                                                    className="text-dark p-0 fw-normal"
                                                    style={{ backgroundColor: bgColor, borderColor }}
                                                    title={tooltip}
                                                >
                                                    BL
                                                </th>
                                                <th
                                                    className="text-dark p-0 fw-normal"
                                                    style={{ backgroundColor: bgColor, borderColor }}
                                                    title={tooltip}
                                                >
                                                    NBL
                                                </th>
                                            </React.Fragment>
                                        );
                                    })}
                                </tr>


                            </thead>
                            <tbody>
                                {employees.map((emp) => (
                                    <tr key={emp.id}>
                                        {days.map((day, j) => {
                                            const formatted = format(day, "yyyy-MM-dd");
                                            const isHoliday = holidayDates.includes(formatted);
                                            const isWeekly = isWeeklyHoliday(day);

                                            const logs = filteredWorklogs.filter(
                                                (log) => log.employeeId === emp.id && isSameDay(new Date(log.date), day)
                                            );

                                            const billable = logs
                                                .filter((l) => l.billingType === "billable")
                                                .reduce((sum, l) => sum + l.hours, 0);
                                            const nonBillable = logs
                                                .filter((l) => l.billingType === "non-billable")
                                                .reduce((sum, l) => sum + l.hours, 0);

                                            const cellStyle = {
                                                backgroundColor: isLeaveForEmployee(emp.id, day) ? "#fff0f0" : isHoliday ? "#d4f8d4" : isWeekly ? "#f7f7f7" : "",
                                                borderColor: isHoliday ? "#e0e0e0" : isWeekly ? "#e0e0e0" : "",
                                            };

                                            const tooltip = isHoliday
                                                ? holidayMap[formatted]
                                                : isWeekly
                                                    ? "Weekly Holiday"
                                                    : "";

                                            return (
                                                <React.Fragment key={j}>
                                                    <td style={cellStyle} title={tooltip}>
                                                        {billable || <span style={{ visibility: "hidden" }}>0</span>}
                                                    </td>
                                                    <td style={cellStyle} title={tooltip}>
                                                        {nonBillable || <span style={{ visibility: "hidden" }}>0</span>}
                                                    </td>
                                                </React.Fragment>
                                            );
                                        })}
                                    </tr>
                                ))}
                            </tbody>
                            <tfoot className="fw-bold">
                                <tr>
                                    {days.map((day, i) => {
                                        const formatted = format(day, "yyyy-MM-dd");
                                        const isHoliday = holidayDates.includes(formatted);
                                        const isWeekly = isWeeklyHoliday(day);

                                        const style = {
                                            backgroundColor: isHoliday ? "#d4f8d4" : isWeekly ? "#f7f7f7" : "",
                                            borderColor: isHoliday ? "#e0e0e0" : isWeekly ? "#e0e0e0" : "",
                                        };

                                        const tooltip = isHoliday
                                            ? holidayMap[formatted]
                                            : isWeekly
                                                ? "Weekly Holiday"
                                                : "";

                                        return (
                                            <React.Fragment key={i}>
                                                <td className="text-dark" style={style} title={tooltip}>
                                                    {getColumnTotal(day, "billable") || <span style={{ visibility: "hidden" }}>0</span>}
                                                </td>
                                                <td className="text-dark" style={style} title={tooltip}>
                                                    {getColumnTotal(day, "non-billable") || <span style={{ visibility: "hidden" }}>0</span>}
                                                </td>
                                            </React.Fragment>
                                        );
                                    })}
                                </tr>
                            </tfoot>

                        </table>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TeamTimesheetGrid;
